import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { format } from "date-fns";
import { 
  User, Mail, ShieldCheck, Building2, Award, 
  CheckCircle, MapPin, PencilLine, Save, X, 
  Activity, CreditCard, ChevronRight, UserPlus, Users, Trash2
} from "lucide-react";
import { collection, onSnapshot, query, where } from "firebase/firestore";
import { auth, db } from "../firebase";

const AccountPage = ({ profile, stats, currentUser, loading }) => {
  const navigate = useNavigate();
  const [isEditing, setIsEditing] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [members, setMembers] = useState([]); // This would be fetched from Firestore 'users' where orgId == currentUser.uid

  // Inside your Component
useEffect(() => {
  if (!auth.currentUser) return;

  console.log("ddd",auth.currentUser.uid)
  // Reference to the main users collection
  const usersRef = collection(db, "users");

  // Query: Find all users where orgUid matches the current Admin's UID
  const q = query(
    usersRef, 
    where("orgUid", "==", auth.currentUser.uid)
  );

  // Subscribe to real-time updates
  const unsubscribe = onSnapshot(q, (querySnapshot) => {
    const membersList = [];
    querySnapshot.forEach((doc) => {
      membersList.push({ id: doc.id, ...doc.data() });
    });
    setMembers(membersList);
  }, (error) => {
    console.error("Registry Fetch Error:", error);
  });

  return () => unsubscribe();
}, [auth.currentUser]);


  if (loading) return <AccountSkeleton />;

  // LOGIC: Check if Organization or Individual
  const isOrg = profile?.profileType === "organization";
  const operatorShortID = currentUser?.uid?.slice(0, 8).toUpperCase() || "PENDING";
  
  // LOGIC: Parse seat limits (e.g., "1-5" -> 5)
  const maxSeats = parseInt(profile?.employeeCount?.split('-')[1]) || 1;

  return (
    <div className="min-h-screen bg-slate-50 font-sans text-slate-900 flex flex-col p-0 md:p-4">
            <div className="flex-1 flex flex-col bg-white border border-slate-300 shadow-2xl overflow-hidden">

      {/* OVERLAY: PROVISIONING MODAL */}

      {/* SECTION 1: STATUTORY HEADER */}
      <div className="flex flex-col md:flex-row items-stretch justify-between border-b border-slate-300">
        <div className="p-8 border-r border-slate-200 flex-1">
          <div className="flex items-baseline gap-4 mb-6">
            <h2 className="text-3xl font-serif font-black text-blue-900 uppercase tracking-tight">
              {isOrg ? "Organization Registry" : "Personnel Registry"}
            </h2>
            <span className="text-xs font-mono text-slate-500 font-bold border-l border-slate-300 pl-3">
              AUTH-v{profile?.version || "4.2.0"}
            </span>
          </div>
          
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 items-center">
            <HeaderStat label="Status" value="Active / Verified" color="text-emerald-600" dot />
            <HeaderStat label="Registry ID" value={`OP-${operatorShortID}`} color="text-blue-900" />
            <HeaderStat label="Entity" value={profile?.organizationName || "FNI"} color="text-slate-700" />
            <HeaderStat label="Last Sync" value={format(new Date(), "yyyy-MM-dd | HH:mm")} color="text-slate-700" isMono />
          </div>
        </div>

        <div className="bg-slate-50 p-8 flex flex-col justify-center gap-4 border-l border-slate-200 w-full md:w-80">
          <button 
            onClick={() => setIsEditing(!isEditing)}
            className={`flex items-center justify-center gap-4 px-6 py-4 font-black text-[11px] uppercase tracking-[0.2em] transition-all shadow-md active:scale-95 border-2 
              ${isEditing ? "bg-white border-red-600 text-red-600" : "bg-blue-900 border-blue-900 text-white hover:bg-blue-950"}`}
          >
            {isEditing ? <X size={14}/> : <PencilLine size={14} className="text-amber-400" />}
            {isEditing ? "Abort Registry Edit" : "Modify Registry"}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-12 flex-1">
        {/* LEFT COLUMN: PRIMARY DOCUMENTATION */}
        <div className="col-span-12 lg:col-span-8 p-10 border-r border-slate-300 space-y-16">
          
          {/* I. IDENTITY SECTION */}
          <section className="space-y-10">
            <SectionHeader title={isOrg ? "I. Organization Identity" : "I. Personnel Identification"} />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
              <AccountField label="Legal Representative" value={profile?.fullName} icon={<User size={14}/>} isEditing={isEditing} />
              <AccountField label="Registered Email" value={profile?.email} icon={<Mail size={14}/>} isEditing={false} />
              <AccountField label="Company Name" value={profile?.organizationName} icon={<Building2 size={14}/>} isEditing={isEditing} />
              <AccountField label="Auth Level" value={profile?.profileType} icon={<ShieldCheck size={14}/>} isEditing={false} />
            </div>
          </section>

          {/* II. ORGANIZATION WORKFORCE (CONDITIONAL) */}
{isOrg && (
  <section className="space-y-10 pt-10 border-t border-slate-100">
    <div className="flex justify-between items-end">
      <div className="space-y-2">
        <SectionHeader title="II. Workforce & Seat Management" />
        <p className="text-[10px] font-bold text-slate-400 uppercase tracking-tight">
          Active Registry Count: {members.length} / {maxSeats} Seats
        </p>
      </div>

      <Link 
        to={members.length >= maxSeats ? "#" : "/dashboard/account/rolemanager"}
        className={members.length >= maxSeats ? "cursor-not-allowed opacity-50" : ""}
      >
        <button 
          disabled={members.length >= maxSeats}
          className="flex items-center gap-2 px-4 py-2 bg-slate-900 text-white text-[10px] font-black uppercase tracking-widest hover:bg-blue-900 disabled:bg-slate-300 transition-all shadow-md"
        >
          <UserPlus size={14} /> Add Member
        </button>
      </Link>
    </div>

    <div className="border border-slate-200 bg-white">
      {/* Table Header */}
      <div className="grid grid-cols-3 bg-slate-50 p-4 border-b border-slate-200 text-[9px] font-black text-slate-400 uppercase tracking-widest">
        <span>Operator / Technician</span>
        <span className="text-center">Role / Level</span>
        <span className="text-right">Registry Status</span>
      </div>

      {/* Admin Row (Your Profile) */}
      <MemberRow 
        name={`${profile?.fullName} (Master)`} 
        email={profile?.email} 
        role="CTO / Admin" 
        isAdmin 
      />

      {/* Dynamically Rendered Workforce */}
      {members.length > 0 ? (
        members.map((m) => (
          <MemberRow 
            key={m.id} 
            name={m.fullName} 
            email={m.email} 
            role={`${m.role} / ${m.ndtLevel}`} 
            status={m.status}
          />
        ))
      ) : (
        <div className="p-8 text-center border-t border-slate-100">
          <p className="text-[10px] font-bold text-slate-300 uppercase tracking-[0.2em]">
            No personnel registered under this node.
          </p>
        </div>
      )}
    </div>
  </section>
)}

          {/* III. TECHNICAL COMPLIANCE */}
          <section className="space-y-10 pt-10 border-t border-slate-100">
            <SectionHeader title={isOrg ? "III. Corporate Compliance" : "II. NDT Compliance & Methods"} />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
              <AccountField label="Certification Level" value={profile?.ndtLevel || "Level II"} icon={<Award size={14}/>} isEditing={isEditing} />
              <AccountField label="Authorized Methods" value={profile?.ndtMethods || "UT, PAUT"} icon={<CheckCircle size={14}/>} isEditing={isEditing} />
              <AccountField label="Region" value={profile?.region || "GLOBAL"} icon={<MapPin size={14}/>} isEditing={isEditing} />
              <AccountField label="Internal ID" value={currentUser?.uid} icon={<Activity size={14}/>} isEditing={false} />
            </div>
          </section>

          {isEditing && (
            <button className="flex items-center gap-3 bg-blue-900 text-white px-10 py-5 text-[11px] font-black uppercase tracking-[0.3em] hover:bg-black transition-all">
              <Save size={16} /> Commit to Master Registry
            </button>
          )}
        </div>

        {/* RIGHT COLUMN: INFRASTRUCTURE */}
        <div className="col-span-12 lg:col-span-4 bg-slate-50/50 p-10 space-y-12">
          
          {/* SUBSCRIPTION DATA */}
          <div className="border-t-4 border-slate-900 pt-8 space-y-6">
            <h4 className="text-[10px] font-black text-slate-400 uppercase tracking-widest">IV. Subscription Status</h4>
            <div className="space-y-1">
              <p className="text-4xl font-serif font-black text-slate-900 uppercase italic italic leading-none">
                {profile?.subscriptionTier || "Standard"}
              </p>
              <p className="text-[10px] text-slate-500 font-bold uppercase tracking-tighter">Account Status: Active</p>
            </div>

            {/* SEAT UTILIZATION */}
            <div className="space-y-4 pt-4">
              <div className="flex justify-between text-[10px] font-black uppercase">
                <span className="text-slate-400">Allocated Seats</span>
                <span className="text-slate-900">{members.length + 1} / {maxSeats}</span>
              </div>
              <div className="w-full h-1 bg-slate-200">
                <div className="h-full bg-blue-900 transition-all duration-700" style={{ width: `${((members.length + 1) / maxSeats) * 100}%` }} />
              </div>
            </div>

            <button onClick={() => navigate('/dashboard/billing')} className="w-full flex items-center justify-between p-5 bg-white border border-slate-300 hover:border-blue-900 transition-all">
              <span className="text-[11px] font-black uppercase tracking-widest text-slate-900">Manage Billing</span>
              <ChevronRight size={16} className="text-slate-300" />
            </button>
          </div>

          <div className="p-6 bg-blue-50 border-l-4 border-blue-900">
            <p className="text-[9px] text-blue-900 font-bold leading-relaxed uppercase">
              Notice: All personnel changes are logged for ISO 9712 auditing. {isOrg ? "Organization maintains liability for provisioned seats." : ""}
            </p>
          </div>
        </div>
      </div>
      </div>
    </div>
  );
};

// --- MODULAR SUBCOMPONENTS ---

const MemberRow = ({ name, email, role, isAdmin }) => (
  <div className="grid grid-cols-3 p-4 border-b border-slate-100 last:border-0 items-center hover:bg-slate-50 transition-all">
    <div className="flex flex-col">
      <span className="text-[11px] font-black text-slate-900 uppercase tracking-tight">{name}</span>
      <span className="text-[10px] font-mono text-slate-400">{email}</span>
    </div>
    <div className="text-center">
      <span className="text-[9px] font-bold px-2 py-1 border border-slate-200 uppercase text-slate-600 tracking-tighter">
        {role}
      </span>
    </div>
    <div className="flex justify-end">
      {isAdmin ? (
        <div className="flex items-center gap-1 text-blue-900"><ShieldCheck size={12}/> <span className="text-[9px] font-black uppercase">Master</span></div>
      ) : (
        <button className="text-slate-300 hover:text-red-600 transition-colors"><Trash2 size={14}/></button>
      )}
    </div>
  </div>
);




const HeaderStat = ({ label, value, color, dot, isMono }) => (
  <div className="space-y-1">
    <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest">{label}</p>
    <div className="flex items-center gap-2">
      {dot && <div className="h-2 w-2 bg-emerald-600 rounded-full" />}
      <span className={`text-[11px] font-bold uppercase ${color} ${isMono ? 'font-mono' : ''}`}>{value}</span>
    </div>
  </div>
);

const SectionHeader = ({ title }) => (
  <h3 className="text-xs font-black text-slate-400 uppercase tracking-[0.25em] border-l-4 border-blue-900 pl-4">
    {title}
  </h3>
);

const AccountField = ({ label, value, icon, isEditing }) => (
  <div className="space-y-3">
    <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest flex items-center gap-2">
      {icon} {label}
    </label>
    {isEditing ? (
      <input 
        type="text" 
        defaultValue={value}
        className="w-full bg-slate-50 border-b-2 border-slate-300 focus:border-blue-900 outline-none px-4 py-3 text-sm font-bold text-slate-800 transition-all"
      />
    ) : (
      <p className="text-sm font-bold text-slate-900 px-4 py-3 bg-slate-50/50 border-b border-transparent">
        {value || "PENDING_ASSIGNMENT"}
      </p>
    )}
  </div>
);

const AccountSkeleton = () => (
  <div className="animate-pulse flex flex-col bg-white border border-slate-300 min-h-screen">
    <div className="h-48 bg-slate-50/50 border-b border-slate-300" />
    <div className="p-10 space-y-12">
      <div className="h-10 w-64 bg-slate-200" />
      <div className="grid grid-cols-2 gap-10">
        <div className="h-16 bg-slate-100" />
        <div className="h-16 bg-slate-100" />
      </div>
    </div>
  </div>
);

export default AccountPage;